// Third party
import React, { Component, ReactNode } from 'react';
import classNames from 'classnames';
import { SortingRule } from 'react-table';

// Components
import DataTable from '../data/DataTable';
import { LoadingSpinner } from '../common';
import { withErrorBoundary } from '../errors';

// Utils
import { formatMessage, FormattedMessage } from '../../../utils/i18n';
import AdvancedSearchItem from '@/types/advanced-search-item';
import AdvancedSearchColumn from '@/types/advanced-search-column';

interface Props {
  data?: AdvancedSearchItem[];
  selected: AdvancedSearchItem[];
  columns: AdvancedSearchColumn[];
  onSortingChange: (sorting: SortingRule[]) => void;
  onRowClick: (id: string) => void;
  className?: string;
  loading?: boolean;
  insideModal?: boolean;
  onLoadMoreResults: () => void;
  hasMore: boolean;
  totalResultsCount?: number;
  useWindowScrollAncestor?: boolean;
  actions?: JSX.Element[];
  onSelectItems: (ids: string[]) => void;
  onUnselectItems: (ids: string[]) => void;
  focusedItem: AdvancedSearchItem;
  itemDetails?: ReactNode;
  selectedTitle?: string;
  labelAccessor: string;
  compact?: boolean;
  canSelect?: boolean;
  customTableActions?: JSX.Element[];
}

class AdvancedSearchResult extends Component<Props> {
  static defaultProps = {
    onSortingChange: () => {},
    canSelect: true,
  };

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.focusedItem && !prevProps.focusedItem) {
      setTimeout(() => {
        this.positionItemDetails();
      }, 100);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  handleScroll = () => {
    this.positionItemDetails();
  };

  positionItemDetails = () => {
    const items = document.getElementsByClassName('rt-tr-group') as HTMLCollection;

    if (!items.length) {
      return;
    }

    const table = document.getElementsByClassName('search-results')[0] as HTMLElement;
    const itemDetails = document.getElementsByClassName('item-details') as HTMLCollection;
    const detailsContainer = document.getElementsByClassName('details-container') as HTMLCollection;
    const lastItem = items[items.length - 1] as HTMLElement;

    const noItemDetails = itemDetails.length === 0 || detailsContainer.length === 0;

    const popup = detailsContainer[0] as HTMLElement;
    const container = itemDetails[0] as HTMLElement;

    if (noItemDetails || lastItem.offsetTop + lastItem.offsetHeight < popup.offsetHeight) {
      return;
    }

    if (table.getBoundingClientRect().top < 0) {
      // Popup is already positioned properly.
      if (popup.style.position === 'fixed') {
        return;
      }

      const width = `${container.getBoundingClientRect().width}px`;

      popup.style.position = 'fixed';
      container.style.width = width;
      container.style.minWidth = width;
      popup.style.width = width;
    } else {
      popup.style.removeProperty('position');
      container.style.removeProperty('width');
      container.style.removeProperty('minWidth');
      popup.style.removeProperty('width');
    }
  };

  getActions = () => {
    const { customTableActions } = this.props;

    if (customTableActions !== undefined) {
      return customTableActions;
    }

    return !this.props.insideModal ? this.props.actions : undefined;
  };

  getColumns = () => {
    const { columns, focusedItem, compact } = this.props;

    if (focusedItem || compact) {
      return columns.filter(c => c.includeInCompact);
    }
    return columns;
  };

  formatData = () => {
    const { focusedItem } = this.props;

    if (!this.props.data) {
      return [];
    }

    return this.props.data.map(i => ({
      ...i,
      isFocused: focusedItem && focusedItem._id === i._id,
    }));
  };

  render() {
    const {
      className,
      loading,
      data,
      totalResultsCount,
      useWindowScrollAncestor,
      hasMore,
      selected,
      focusedItem,
      itemDetails,
      selectedTitle,
      labelAccessor,
      canSelect,
    } = this.props;
    const tableIsHidden = loading || !this.props.data || this.props.data.length === 0;

    const classes = classNames('search-results d-flex justify-content-center', className, {
      'has-details': !!focusedItem,
    });

    return (
      <div className={classes} data-testid="legacy-advanced-search-result">
        {loading && <LoadingSpinner message={formatMessage('Common.LoadingResults')} />}

        {/* No data at matching search */}
        {!loading && this.props.data && this.props.data.length === 0 && (
          <div className="empty-results">
            <h1>
              <FormattedMessage id="AdvancedSearch.NoResults.Title" />
            </h1>
            <span className="empty-results-text">
              <FormattedMessage id="AdvancedSearch.NoResults.Text" />
            </span>
          </div>
        )}

        <DataTable
          className={classNames('results-table flex-grow-1', { hidden: tableIsHidden })}
          columns={this.getColumns()}
          data={this.formatData()}
          canSelect={canSelect}
          selected={selected}
          onSelectItems={this.props.onSelectItems}
          onUnselectItems={this.props.onUnselectItems}
          sortable
          serverSideSorting
          focusedId={focusedItem && focusedItem._id}
          onSortingChange={this.props.onSortingChange}
          selectedTitle={selectedTitle}
          selectedActions={this.getActions()}
          onRowClick={this.props.onRowClick}
          showLoadMore={data && totalResultsCount != null && data.length < totalResultsCount && hasMore}
          onLoadMore={this.props.onLoadMoreResults}
          labelAccessor={labelAccessor}
          scrollAncestor={useWindowScrollAncestor ? window : undefined}
        />

        {!!focusedItem && itemDetails}
      </div>
    );
  }
}

export default withErrorBoundary(AdvancedSearchResult);
